if (!customElements.get('product-buy-together')) {
  customElements.define(
    'product-buy-together',
    class ProductBuyTogether extends HTMLElement {
      constructor() {
        super();

        this.isSubmitting = false;
        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
      }

      connectedCallback() {
        this.addEventListener('change', this.handleChange);
        this.submitButton?.addEventListener('click', this.handleSubmit);

        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange
        );

        this.updateState();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.handleChange);
        this.submitButton?.removeEventListener('click', this.handleSubmit);
        this.variantChangeUnsubscriber?.();
      }

      get submitButton() {
        return this.querySelector('.product-buy-together__submit');
      }

      get buttonText() {
        return this.querySelector('[data-buy-together-button-text]');
      }

      get errorMessage() {
        return this.querySelector('.product-form__error-message');
      }

      get currentItem() {
        return this.querySelector('[data-buy-together-current]');
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      get selectedItems() {
        return [...this.querySelectorAll('[data-product-buy-together-item]')].filter((item) => {
          const checkbox = item.querySelector('[data-buy-together-checkbox]');
          return checkbox?.checked && item.dataset.available === 'true';
        });
      }

      handleChange(event) {
        if (!event.target.matches('[data-buy-together-checkbox]')) return;

        this.showErrorMessage();
        this.updateState();
      }

      handleVariantChange({ data }) {
        if (`${data.sectionId}` !== this.dataset.sectionId) return;

        const sourceComponent = data.html.querySelector(
          `product-buy-together[data-block-id="${this.dataset.blockId}"]`
        );
        const sourceCurrentItem = sourceComponent?.querySelector('[data-buy-together-current]');
        const currentItem = this.currentItem;

        if (!sourceCurrentItem || !currentItem) return;

        currentItem.className = sourceCurrentItem.className;
        currentItem.dataset.variantId = sourceCurrentItem.dataset.variantId;
        currentItem.dataset.price = sourceCurrentItem.dataset.price;
        currentItem.dataset.available = sourceCurrentItem.dataset.available;
        currentItem.innerHTML = sourceCurrentItem.innerHTML;

        this.showErrorMessage();
        this.updateState();
      }

      updateState() {
        const total = [...this.querySelectorAll('[data-product-buy-together-item]')].reduce((sum, item) => {
          const checkbox = item.querySelector('[data-buy-together-checkbox]');
          return checkbox?.checked ? sum + Number(item.dataset.price || 0) : sum;
        }, 0);

        if (this.buttonText) {
          const formattedTotal = FoxTheme.Currency.formatMoney(total, FoxTheme.settings.moneyFormat);
          this.buttonText.textContent = `${this.dataset.buttonLabel} — ${formattedTotal}`;
        }

        const currentIsAvailable = this.currentItem?.dataset.available === 'true';
        const shouldDisable = this.isSubmitting || !currentIsAvailable || this.selectedItems.length === 0;

        if (shouldDisable) {
          this.submitButton?.setAttribute('disabled', 'true');
          this.submitButton?.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton?.removeAttribute('disabled');
          this.submitButton?.removeAttribute('aria-disabled');
        }
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (this.isSubmitting || this.submitButton?.disabled) return;

        const items = this.selectedItems.map((item) => ({
          id: Number(item.dataset.variantId),
          quantity: 1,
        }));

        if (items.length === 0) return;

        this.lastSubmittedElement = event.currentTarget;
        this.showErrorMessage();
        this.setLoading(true);

        const shouldOpenDrawer =
          !document.body.classList.contains('cart-template') &&
          FoxTheme.settings.cartType === 'drawer' &&
          this.cartDrawer;
        const sections = [];

        if (shouldOpenDrawer) {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
          );
        }

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          items,
          ...(shouldOpenDrawer
            ? {
                sections,
                sections_url: window.location.pathname,
              }
            : {}),
        });

        try {
          const response = await fetch(FoxTheme.routes.cart_add_url, config);
          const parsedState = await response.json();

          if (!response.ok || parsedState.status) {
            throw new Error(parsedState.description || this.dataset.errorMessage);
          }

          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: { product: parsedState },
            })
          );

          if (shouldOpenDrawer) {
            const cartResponse = await fetch(FoxTheme.routes.cart_url, {
              ...FoxTheme.utils.fetchConfig('json', 'GET'),
            });
            const cart = await cartResponse.json();
            cart.sections = parsedState.sections;

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
            this.cartDrawer.show(this.lastSubmittedElement);
          } else {
            window.location.href = FoxTheme.routes.cart_url;
          }
        } catch (error) {
          console.error(error);
          this.showErrorMessage(error.message || this.dataset.errorMessage);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        this.isSubmitting = isLoading;
        this.submitButton?.classList.toggle('btn--loading', isLoading);
        this.updateState();
      }

      showErrorMessage(message = '') {
        if (!this.errorMessage) return;

        this.errorMessage.hidden = !message;
        this.errorMessage.textContent = message;
      }
    }
  );
}
